import URI from 'urijs'
import Vue from 'vue'
import axios from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { config } from '@/plugins/env.config'

async function getInternalToken(user, attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('oauth')
  getTokenUri.segment('token')
  getTokenUri.addSearch('username', user.username.toLowerCase())
  getTokenUri.addSearch('password', user.password)
  getTokenUri.addSearch('grant_type', 'password')
  
  const getTokenHeaders = {
    Authorization: `Basic ${Buffer.from(`${config.clientId}:${config.clientSecret}`).toString('base64')}`
  }

  let tokenResponse
  try {
    tokenResponse = await axios.post(getTokenUri.toString(), {}, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getInternalToken(user.username, user.password, attemptNumber + 1)
    }

    throw error
  }

  const token = camelizeKeys(tokenResponse.data)
  if (!token || !token.accessToken || !token.tokenType) {
    throw new Error('Invalid server response')
  }

  return {
    
    accessToken: token.accessToken,
    tokenType: token.tokenType,
    refreshToken: token.refreshToken,
    extensions: token.extensions,
    passwordRotationRequired: token.passwordRotationRequired,
    termsAndConditionsAcceptanceRequired: token.termsAndConditionsAcceptanceRequired,
    tfaRequired: token.tfaRequired,
    tfaAvailableMethods: token.tfaAvailableMethods
  }
}

async function requestPasswordChange(email, attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('users')
  getTokenUri.segment('request-password-change')

  const getTokenHeaders = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try {
    email.email = email.email.toLowerCase();
    return await axios.post(getTokenUri.toString(), email, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return requestPasswordChange(email, attemptNumber + 1)
    }

    throw error
  }
}

async function getClientToken(attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('oauth')
  getTokenUri.segment('token')
  getTokenUri.addSearch('grant_type', 'client_credentials')

  const getTokenHeaders = {
    Authorization: `Basic ${Buffer.from(`${config.clientId}:${config.clientSecret}`).toString('base64')}`
  }

  let tokenResponse
  try {
    tokenResponse = await axios.post(getTokenUri.toString(), {}, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getInternalToken(attemptNumber + 1)
    }

    throw error
  }

  const token = camelizeKeys(tokenResponse.data)
  if (!token || !token.accessToken || !token.tokenType) {
    throw new Error('Invalid server response')
  }

  return {
    accessToken: token.accessToken,
    tokenType: token.tokenType
  }
}

async function changePassword(password, token, attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('users')
  getTokenUri.segment('change-password')

  const getTokenHeaders = {
    Authorization: `Bearer ${token}`
  }

  try {
    return await axios.post(getTokenUri.toString(), { password: password }, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return changePassword(password, token, attemptNumber + 1)
    }

    throw error
  }
}

async function validateToken(token, attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('oauth')
  getTokenUri.segment('check_token')
  getTokenUri.addQuery({token})

  const getTokenHeaders = {
    Authorization: `Basic ${Buffer.from(`${config.clientId}:${config.clientSecret}`).toString('base64')}`
  }

  try {
    return await axios.get(getTokenUri.toString(), {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return validateToken(token, attemptNumber + 1)
    }

    throw error
  }
}

async function updatePassword(previousPassword, newPassword) {

  try {
    const payload = decamelizeKeys({ previousPassword, newPassword });
    return await Vue.prototype.$http.post(`${config.apiBaseUrl}/v1/users/update-password`, payload)

  } catch (error) {

    console.log(error);
    throw error
  }
}


async function acceptTermsAndConditions(user, attemptNumber = 1) {
  const getUri = new URI(config.apiBaseUrl)
  getUri.segment('v1')
  getUri.segment('users')
  getUri.segment(user.id)
  getUri.segment('accept-terms-and-conditions')

  let tokenResponse
  try {
    tokenResponse = await Vue.prototype.$http.post(getUri.toString(), { terms_and_conditions_slug: user.slug })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return acceptTermsAndConditions(user, attemptNumber + 1)
    }

    throw error
  }

  const token = camelizeKeys(tokenResponse.data)
  if (!token || !token.accessToken || !token.tokenType) {
    throw new Error('Invalid server response')
  }

  return {
    accessToken: token.accessToken,
    tokenType: token.tokenType,
    refreshToken: token.refreshToken,
    extensions: token.extensions,
    termsAndConditionsAcceptanceRequired: token.termsAndConditionsAcceptanceRequired,
    tfaRequired: token.tfaRequired,
    tfaAvailableMethods: token.tfaAvailableMethods
  }
}

async function refreshToken(refreshToken, attemptNumber = 1) {
  const getTokenUri = new URI(config.apiBaseUrl)
  getTokenUri.segment('v1')
  getTokenUri.segment('oauth')
  getTokenUri.segment('token')
  getTokenUri.addSearch('grant_type', 'refresh_token')
  getTokenUri.addSearch('refresh_token', refreshToken)

  const getTokenHeaders = {
    Authorization: `Basic ${Buffer.from(`${config.clientId}:${config.clientSecret}`).toString('base64')}`
  }

  let tokenResponse
  try {
    tokenResponse = await axios.post(getTokenUri.toString(), {}, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return refreshToken(refreshToken, attemptNumber + 1)
    }

    throw error
  }

  const token = camelizeKeys(tokenResponse.data)
  if (!token || !token.accessToken || !token.tokenType) {
    throw new Error('Invalid server response')
  }

  return {
    accessToken: token.accessToken,
    tokenType: token.tokenType,
    refreshToken: token.refreshToken,
    extensions: token.extensions,
    termsAndConditionsAcceptanceRequired: token.termsAndConditionsAcceptanceRequired,
    tfaRequired: token.tfaRequired,
    tfaAvailableMethods: token.tfaAvailableMethods
  }
}

async function sendEmailVerificatinCode(user, attemptNumber = 1) {
  const getUri = new URI(config.apiBaseUrl)
  getUri.segment('v1')
  getUri.segment('users')
  getUri.segment(user.id)
  getUri.segment('two-factor-authentication')
  getUri.segment('send-email-verification-code')

  const getTokenHeaders = {
    Authorization: `Bearer ${user.token}`
  }

  try {
    return await  Vue.prototype.$http.post(getUri.toString(), {}, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendEmailVerificatinCode(user, attemptNumber + 1)
    }
    throw error
  }
}

async function validatingEmailVerificatinCode(payload,attemptNumber = 1) {
  const getUri = new URI(config.apiBaseUrl)
  getUri.segment('v1')
  getUri.segment('users')
  getUri.segment(payload.id)
  getUri.segment('two-factor-authentication')
  getUri.segment('validate-email-verification-code')

  try {
    return await  Vue.prototype.$http.post(getUri.toString(), {code:payload.tfaCode}, {
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return validatingEmailVerificatinCode(payload, attemptNumber + 1)
    }
    throw error
  }
}
async function sendSmsTfaCode(user, attemptNumber = 1) {
  const getUri = new URI(config.apiBaseUrl)
  getUri.segment('v1')
  getUri.segment('users')
  getUri.segment(user.id)
  getUri.segment('two-factor-authentication')
  getUri.segment('send-sms-verification-code')

  const getTokenHeaders = {
    Authorization: `Bearer ${user.token}`
  }

  try {
    return await  Vue.prototype.$http.post(getUri.toString(), {}, {
      headers: getTokenHeaders
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendSmsTfaCode(user, attemptNumber + 1)
    }
    throw error
  }
}
async function validateSmsTfaCode(payload,attemptNumber = 1) {
  const getUri = new URI(config.apiBaseUrl)
  getUri.segment('v1')
  getUri.segment('users')
  getUri.segment(payload.id)
  getUri.segment('two-factor-authentication')
  getUri.segment('validate-sms-verification-code')

  try {
    return await  Vue.prototype.$http.post(getUri.toString(), {code:payload.tfaCode}, {
    })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return validateSmsTfaCode(payload, attemptNumber + 1)
    }
    throw error
  }
}


export default {
  acceptTermsAndConditions,
  changePassword,
  getClientToken,
  getInternalToken,
  refreshToken,
  requestPasswordChange,
  updatePassword,
  validateToken,
  validatingEmailVerificatinCode,
  sendEmailVerificatinCode,
  sendSmsTfaCode,
  validateSmsTfaCode
}
